// Erlang WebSocket Service for High-Performance Chat
// This service connects to the Erlang WebSocket server for ultra-fast message delivery

class ErlangWebSocketService {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private isConnecting = false;
  private messageHandlers: Map<string, Function[]> = new Map();
  private typingThrottleTimeout: NodeJS.Timeout | null = null;

  constructor() {
    this.connect = this.connect.bind(this);
    this.disconnect = this.disconnect.bind(this);
    this.sendMessage = this.sendMessage.bind(this);
    this.joinRoom = this.joinRoom.bind(this);
    this.leaveRoom = this.leaveRoom.bind(this);
  }

  connect(roomId: string, userId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.isConnecting || (this.ws && this.ws.readyState === WebSocket.OPEN)) {
        resolve();
        return;
      }

      this.isConnecting = true;
      const wsUrl = `${process.env.REACT_APP_ERLANG_WS_URL || 'ws://localhost:8081'}/ws/chat/${roomId}/${userId}`;
      
      try {
        this.ws = new WebSocket(wsUrl);
        
        this.ws.onopen = () => {
          this.isConnecting = false;
          this.reconnectAttempts = 0;
          
          // Join the room
          this.joinRoom(userId);
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            this.handleMessage(data);
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
          }
        };

        this.ws.onclose = (event) => {
          this.isConnecting = false;
          this.ws = null;
          
          // Attempt to reconnect if not intentionally closed
          if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.scheduleReconnect(roomId, userId);
          }
        };

        this.ws.onerror = (error) => {
          this.isConnecting = false;
          reject(error);
        };

      } catch (error) {
        this.isConnecting = false;
        reject(error);
      }
    });
  }

  private scheduleReconnect(roomId: string, userId: string) {
    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
    
    setTimeout(() => {
      this.connect(roomId, userId).catch(console.error);
    }, delay);
  }

  disconnect() {
    if (this.ws) {
      this.ws.close(1000, 'Intentional disconnect');
      this.ws = null;
    }
    this.reconnectAttempts = this.maxReconnectAttempts; // Prevent auto-reconnect
  }

  send(data: any) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }

  joinRoom(userId: string, userName?: string) {
    this.send({
      type: 'join',
      user_id: userId,
      user_name: userName || `User ${userId}`
    });
  }

  leaveRoom(userId: string) {
    this.send({
      type: 'leave',
      user_id: userId
    });
  }

  sendMessage(content: string, userId: string, messageType: string = 'text') {
    this.send({
      type: 'message',
      user_id: userId,
      content: content,
      message_type: messageType
    });
  }

  sendTypingStart(userId: string) {
    // Throttle typing events to prevent spam
    if (this.typingThrottleTimeout) {
      return;
    }
    
    this.send({
      type: 'typing_start',
      user_id: userId
    });
    
    this.typingThrottleTimeout = setTimeout(() => {
      this.typingThrottleTimeout = null;
    }, 200);
  }

  sendTypingStop(userId: string) {
    this.send({
      type: 'typing_stop',
      user_id: userId
    });
  }

  getMessages() {
    this.send({
      type: 'get_messages'
    });
  }

  getUsers() {
    this.send({
      type: 'get_users'
    });
  }

  private handleMessage(data: any) {
    const { type } = data;
    
    // Emit to registered handlers
    const handlers = this.messageHandlers.get(type) || [];
    handlers.forEach(handler => {
      try {
        handler(data);
      } catch (error) {
        console.error(`Error in message handler for ${type}:`, error);
      }
    });
  }

  // Event handling
  on(event: string, handler: Function) {
    if (!this.messageHandlers.has(event)) {
      this.messageHandlers.set(event, []);
    }
    this.messageHandlers.get(event)!.push(handler);
  }

  off(event: string, handler: Function) {
    const handlers = this.messageHandlers.get(event) || [];
    const index = handlers.indexOf(handler);
    if (index > -1) {
      handlers.splice(index, 1);
    }
  }

  // Utility methods
  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  getConnectionState(): string {
    if (!this.ws) return 'disconnected';
    
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING: return 'connecting';
      case WebSocket.OPEN: return 'connected';
      case WebSocket.CLOSING: return 'closing';
      case WebSocket.CLOSED: return 'closed';
      default: return 'unknown';
    }
  }

  // Health check
  ping() {
    this.send({ type: 'ping' });
  }
}

// Create singleton instance
export const erlangWebSocketService = new ErlangWebSocketService();
export default erlangWebSocketService;
